import { z } from 'zod'
import {
  DiditModeSchema,
  DiditOutcomeSchema,
  DistrictStatusSchema,
  GenderSchema,
  MemberStatusSchema,
  ReviewActionSchema,
  ReviewDecisionSchema,
  ReviewReasonCodeSchema,
  VoteStatusSchema,
} from '../enums'
import {
  EthAddressSchema,
  IsoDateSchema,
  IsoDateTimeSchema,
  MemberIdSchema,
  ProcessIdSchema,
  UuidSchema,
} from '../primitives'
import {
  AssuranceBadgesSchema,
  MemberAssuranceSchema,
  MemberExemptionsSchema,
  ModuleStateSchema,
} from '../domain/assurance'
import { PublicAreaSchema } from '../domain/geo'
import { ModuleIdSchema } from '../domain/requirements'
import { ResidenceDecisionSchema, ResidenceVerdictSchema } from '../domain/residence'
import { PaginationQuerySchema } from './common'
import { ContactVerificationSchema } from './contact'
import { DiditSummarySchema, GpsReadingSchema, VoteGpsEntrySchema } from './kyc'

/**
 * Organizer-side member management (re-keyed from v2 "voters"): `/api/org/organizations/:orgId/members[...]`.
 * One member row per person per organization; uniqueness of email, national ID and phone is per organization.
 */

/* ================================================================== shared pieces */

export const ReviewReasonSchema = z.object({
  code: ReviewReasonCodeSchema,
  message: z.string(),
  /** e.g. Didit warning codes, matched member ids, computed age. */
  details: z.unknown().optional(),
  at: IsoDateTimeSchema,
})
export type ReviewReason = z.infer<typeof ReviewReasonSchema>

/** Organizer's terminal manual decision (M4), "request more info", removal, re-admission or reset. */
export const ReviewRecordSchema = z.object({
  action: z.union([ReviewActionSchema, z.enum(['remove', 'readmit', 'reset'])]),
  decision: ReviewDecisionSchema.nullable(),
  note: z.string().nullable(),
  organizerId: UuidSchema,
  organizerName: z.string().nullable(),
  at: IsoDateTimeSchema,
  /** Review reasons the decision settled (they leave the member's open reasons, so the review queue drops them). */
  resolvedReasons: z.array(ReviewReasonCodeSchema).optional(),
})
export type ReviewRecord = z.infer<typeof ReviewRecordSchema>

/** The member's latest vote on one question of one voting. */
export const VoteRecordSchema = z.object({
  electionId: UuidSchema,
  questionId: UuidSchema,
  processId: ProcessIdSchema,
  voteId: z.string(),
  /** VoteResult.node — the sequencer URL the vote went to (our /api/seq proxy); reused for revotes. */
  node: z.string(),
  status: VoteStatusSchema,
  /** Number of times this member (re)voted on this question. */
  submissions: z.number().int().min(1),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})
export type VoteRecord = z.infer<typeof VoteRecordSchema>

/* ================================================================== list */

export const MEMBER_SORTS = ['created_desc', 'created_asc', 'updated_desc', 'name_asc'] as const

const csvStatuses = z
  .string()
  .optional()
  .transform((v) => (v ? v.split(',').filter(Boolean) : undefined))
  .pipe(z.array(MemberStatusSchema).optional())

/* GET /api/org/organizations/:orgId/members?status=&q=&district=&districtStatus=&needsReview=&page=&pageSize=&sort= */
export const MemberListQuerySchema = PaginationQuerySchema.extend({
  /** Comma-separated list accepted: `status=in_review,retry_allowed`. Default: every status except `revoked`. */
  status: csvStatuses,
  /** Free-text search over name, email, national id, phone, locality. */
  q: z.string().trim().max(200).optional(),
  district: z.string().trim().max(200).optional(),
  districtStatus: DistrictStatusSchema.optional(),
  needsReview: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
  sort: z.enum(MEMBER_SORTS).default('created_desc'),
})
export type MemberListQuery = z.input<typeof MemberListQuerySchema>

export const MemberListItemSchema = z.object({
  id: MemberIdSchema,
  status: MemberStatusSchema,
  fullName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  nationality: z.string().nullable(),
  /** Null when the organization does not ask for it (PLAN v4). */
  locality: z.string().nullable(),
  district: z.string().nullable(),
  districtStatus: DistrictStatusSchema,
  /** What the member proved vs the CURRENT requirements (PLAN v4 R17: Email, Phone, Human, ID, Citizen, Residence). */
  assurance: AssuranceBadgesSchema,
  kycAttempts: z.number().int().min(0),
  /** Latest Didit status / our outcome. */
  lastKycStatus: z.string().nullable(),
  lastKycOutcome: DiditOutcomeSchema.nullable(),
  faceMatchScore: z.number().nullable(),
  livenessScore: z.number().nullable(),
  ipCountry: z.string().nullable(),
  vpn: z.boolean().nullable(),
  reviewReasons: z.array(ReviewReasonSchema),
  hasAccount: z.boolean(),
  emailOptOut: z.boolean(),
  /** Distinct votings this member voted in. */
  votedElections: z.number().int().min(0),
  removedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})
export type MemberListItem = z.infer<typeof MemberListItemSchema>

export const MemberListResponseSchema = z.object({
  items: z.array(MemberListItemSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
})
export type MemberListResponse = z.infer<typeof MemberListResponseSchema>

/*
 * GET /api/org/organizations/:orgId/review-queue?page=&pageSize= → MemberListResponse
 * Members waiting for the organizer: status in_review, or registered / approved / active members with open review
 * reasons (e.g. district_unresolved). Oldest first.
 */
export const ReviewQueueQuerySchema = PaginationQuerySchema
export type ReviewQueueQuery = z.input<typeof ReviewQueueQuerySchema>

/* ================================================================== detail (verbose) */

export const KycSessionDetailSchema = z.object({
  /** Didit session_id */
  id: z.string(),
  /** 1-based attempt number for this member (max MAX_KYC_ATTEMPTS). */
  attempt: z.number().int().min(1),
  /** Didit status verbatim. */
  status: z.string(),
  outcome: DiditOutcomeSchema,
  /** Hosted verification URL (only while the session can still be completed). */
  url: z.string().nullable(),
  summary: DiditSummarySchema.nullable(),
  /** Didit application the session was created on (`sandbox` for test organizations and sandbox deployments). */
  diditApp: DiditModeSchema,
  /** Test organizations with a random outcome: the sandbox scenario armed at creation (null otherwise). */
  testScenario: z.string().nullable(),
  /** Didit events / status changes received for this session (webhook or poller). */
  events: z.array(
    z.object({
      at: IsoDateTimeSchema,
      source: z.enum(['webhook', 'poller', 'manual', 'api']),
      status: z.string(),
      /** True when the event was logged but not applied (manual decision is terminal, M4). */
      ignored: z.boolean(),
    })
  ),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})
export type KycSessionDetail = z.infer<typeof KycSessionDetailSchema>

export const LocationPointSchema = z.object({
  kind: z.enum(['gps_registration', 'gps_vote', 'ip', 'document', 'poa']),
  label: z.string(),
  lat: z.number(),
  lng: z.number(),
  accuracyM: z.number().nullable(),
  at: IsoDateTimeSchema.nullable(),
})
export type LocationPoint = z.infer<typeof LocationPointSchema>

/* GET /api/org/organizations/:orgId/members/:memberId → MemberDetailResponse (verbose; requirement 16) */
export const MemberDetailSchema = z.object({
  id: MemberIdSchema,
  organizationId: UuidSchema,
  status: MemberStatusSchema,
  /** CSP census index (ballot slot), stable per person in the organization (M1). */
  cspIndex: z.number().int().min(0),
  /** Self-declared at registration (the organization collected it). National id is shown in full to the organizer. */
  declared: z.object({
    fullName: z.string(),
    /** Null when the organization did not ask for it (PLAN v4: no document and no age limits). */
    dateOfBirth: IsoDateSchema.nullable(),
    /** Null when the organization did not ask for it (no document check). */
    nationalId: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    locality: z.string().nullable(),
    nationality: z.string().nullable(),
    gender: GenderSchema.nullable(),
    consentAt: IsoDateTimeSchema,
  }),
  /** Age today from the document DOB (else declared). The age at each voting start is checked by the CSP. */
  age: z.object({
    value: z.number().int().nullable(),
    referenceDate: IsoDateSchema,
    source: z.enum(['document', 'declared', 'unknown']),
  }),
  district: z.object({
    locality: z.string().nullable(),
    district: z.string().nullable(),
    status: DistrictStatusSchema,
    /** Locality history (members may update it). */
    history: z.array(z.object({ locality: z.string(), district: z.string().nullable(), at: IsoDateTimeSchema })),
  }),
  location: z.object({
    registrationGps: GpsReadingSchema.nullable(),
    /** One entry per recorded vote submission with GPS (only appended when status=ok). */
    voteGps: z.array(VoteGpsEntrySchema),
    /** Points ready to draw on a map (ip, document address, gps). */
    points: z.array(LocationPointSchema),
    /** Pairwise distances in km between the available points. */
    distancesKm: z.object({
      ipToDocument: z.number().nullable(),
      gpsToDocument: z.number().nullable(),
      gpsToIp: z.number().nullable(),
    }),
  }),
  /** Email / phone checks made in our registration form before the Didit session. */
  contactVerification: ContactVerificationSchema.nullable(),
  /** What the member proved (members.assurance, PLAN v4 §1.2); null = nothing recorded. */
  assurance: MemberAssuranceSchema.nullable(),
  /** Modules the organizer exempted this member from (R6). */
  exemptions: MemberExemptionsSchema.nullable(),
  /** Live compliance with the CURRENT requirements (what the CSP eligibility uses). */
  compliance: z.object({
    modules: z.record(ModuleIdSchema, ModuleStateSchema),
    /** Residence decision recomputed from the stored evidence (null when off / exempt / manual / no evidence). */
    residence: ResidenceDecisionSchema.nullable(),
    nationality: z.string().nullable(),
  }),
  /** Latest Didit decision digest (also inside kycSessions[]). */
  didit: DiditSummarySchema.nullable(),
  kycSessions: z.array(KycSessionDetailSchema),
  reviewReasons: z.array(ReviewReasonSchema),
  reviewNotes: z.string().nullable(),
  reviews: z.array(ReviewRecordSchema),
  account: z.object({
    walletAddress: EthAddressSchema.nullable(),
    createdAt: IsoDateTimeSchema.nullable(),
    lastLoginAt: IsoDateTimeSchema.nullable(),
    recoveredAt: IsoDateTimeSchema.nullable(),
  }),
  /** Organizer removal (null when not removed). */
  removal: z
    .object({
      removedAt: IsoDateTimeSchema,
      reason: z.string().nullable(),
      removedBy: z.object({ id: UuidSchema, name: z.string().nullable() }).nullable(),
      /** Status restored by a re-admission. */
      previousStatus: MemberStatusSchema,
    })
    .nullable(),
  email: z.object({ optOut: z.boolean(), optOutAt: IsoDateTimeSchema.nullable() }),
  /** Residence declarations, one per voting (M2): the latest declaration made at a CSP attestation. */
  residenceDeclarations: z.array(
    z.object({
      electionId: UuidSchema,
      electionTitle: z.string(),
      days: z.number().int().min(1),
      declaredAt: IsoDateTimeSchema,
    })
  ),
  votes: z.array(
    VoteRecordSchema.extend({
      electionTitle: z.string(),
      questionTitle: z.string(),
      /** True when this ballot was cast with the residence declaration; null when the voting does not ask for it. */
      residenceDeclared: z.boolean().nullable(),
    })
  ),
  /**
   * Privacy (services/privacy.ts): personal data cut to the minimum after the final decision (`minimizedAt`; document
   * names / numbers, street addresses, IP / device data are gone and show as "—"), or erased at the member's request.
   */
  privacy: z
    .object({
      minimizedAt: IsoDateTimeSchema.nullable(),
      erasedAt: IsoDateTimeSchema.nullable(),
      /** The member's Didit verification data was deleted at Didit (images / video no longer exist). */
      diditDeleted: z.boolean(),
    })
    .optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})
export type MemberDetail = z.infer<typeof MemberDetailSchema>

export const MemberDetailResponseSchema = z.object({ member: MemberDetailSchema })
export type MemberDetailResponse = z.infer<typeof MemberDetailResponseSchema>

/* ================================================================== organizer actions (→ MemberDetailResponse) */

/*
 * POST …/members/:memberId/review
 * approve / reject are terminal (M4) and are mirrored to Didit (PATCH update-status).
 * request_info sets retry_allowed and shows `note` to the member (note required).
 */
export const ReviewRequestSchema = z
  .object({
    action: ReviewActionSchema,
    note: z.string().trim().max(2000).optional(),
    /** `approve_same_person` (R2): the revoked / removed member whose census slot this member takes over. */
    matchedMemberId: MemberIdSchema.optional(),
  })
  .refine((v) => v.action !== 'request_info' || (v.note && v.note.length > 0), {
    message: 'A note for the member is required when requesting more information',
    path: ['note'],
  })
  .refine((v) => v.action !== 'approve_same_person' || v.matchedMemberId !== undefined, {
    message: 'The matched member is required',
    path: ['matchedMemberId'],
  })
export type ReviewRequest = z.infer<typeof ReviewRequestSchema>

/* PATCH …/members/:memberId/district (sets districtStatus = manual; takes effect at the next CSP request, S8). */
export const SetDistrictRequestSchema = z.object({
  district: z.string().trim().min(1).max(200).nullable(),
  /** Optionally correct the locality spelling at the same time. */
  locality: z.string().trim().min(1).max(200).optional(),
})
export type SetDistrictRequest = z.infer<typeof SetDistrictRequestSchema>

/*
 * POST …/members/:memberId/remove → status `removed` immediately: no login, no CSP attestation, no emails. Ballots
 * already settled on chain stay (documented). 409 invalid_state_transition when already removed / revoked.
 */
export const RemoveMemberRequestSchema = z.object({ reason: z.string().trim().max(2000).optional() })
export type RemoveMemberRequest = z.infer<typeof RemoveMemberRequestSchema>

/*
 * POST …/members/:memberId/readmit → back to the status before the removal (usually `active`, same wallet, same
 * census slot: a new ballot overwrites the old one, M1). 409 invalid_state_transition unless `removed`.
 */
export const ReadmitMemberRequestSchema = z.object({ note: z.string().trim().max(2000).optional() })
export type ReadmitMemberRequest = z.infer<typeof ReadmitMemberRequestSchema>

/*
 * POST …/members/:memberId/reset → status `revoked`: voids the wallet binding (lost password AND words); the person
 * must register + verify again; their census slot is kept (M1).
 */
export const ResetMemberRequestSchema = z.object({ note: z.string().trim().max(2000).optional() })
export type ResetMemberRequest = z.infer<typeof ResetMemberRequestSchema>

/*
 * GET …/members/:memberId/kyc/:sessionId/decision → LiveDecisionResponse
 * Re-fetches the decision from Didit for its fresh presigned image / video URLs (M6). Nothing is stored, the decision
 * JSON itself is not returned (data minimization, H8), and only while the member is under review (`in_review`,
 * `retry_allowed`; else 403 forbidden).
 */
export const LiveDecisionResponseSchema = z.object({
  sessionId: z.string(),
  fetchedAt: IsoDateTimeSchema,
  /** Image URLs extracted for convenience (expire after ~4 h). */
  images: z.array(z.object({ label: z.string(), url: z.string() })),
})
export type LiveDecisionResponse = z.infer<typeof LiveDecisionResponseSchema>

/* ================================================================== members map */

/* GET /api/org/organizations/:orgId/map?status=&district= → MemberMapResponse */
export const MemberMapQuerySchema = z.object({
  /** Comma-separated statuses. Default: every status except `revoked`. */
  status: csvStatuses,
  district: z.string().trim().max(200).optional(),
})
export type MemberMapQuery = z.input<typeof MemberMapQuerySchema>

/** One member at their best known location: GPS (registration) > proof of address > document address > IP. */
export const MemberMapPointSchema = z.object({
  memberId: MemberIdSchema,
  fullName: z.string(),
  status: MemberStatusSchema,
  district: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
  source: z.enum(['gps', 'poa', 'document', 'ip']),
  accuracyM: z.number().nullable(),
  /** Live residence verdict when the organization checks residence (null otherwise / unknown). */
  residence: ResidenceVerdictSchema.nullable(),
})
export type MemberMapPoint = z.infer<typeof MemberMapPointSchema>

export const MemberMapResponseSchema = z.object({
  /** The organization collects location (else `points` is empty and the UI explains why). */
  collectLocation: z.boolean(),
  points: z.array(MemberMapPointSchema),
  /** Members matching the filters. */
  total: z.number().int().min(0),
  /** Members matching the filters without any location. */
  withoutLocation: z.number().int().min(0),
  /** Residence areas to overlay (simplified; empty when residence is off). OSM attribution required. */
  areas: z.array(PublicAreaSchema),
})
export type MemberMapResponse = z.infer<typeof MemberMapResponseSchema>
